// packages/shared/src/services/user.service.ts
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { COLLECTIONS, User } from '@bookstore/shared';
import { getFirebaseFirestore } from '@bookstore/shared/browser';



export class UserService {
  private get db() {
    return getFirebaseFirestore();
  }

  /**
   * Get user profile by ID
   */
  async getUserProfile(userId: string): Promise<User | null> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return null;
      }

      const data = userDoc.data();
      return {
        uid: userDoc.id,
        ...data,
      } as User;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw new Error('Failed to fetch user profile');
    }
  }

  /**
   * Create user profile (called after authentication)
   */
  async createUserProfile(userId: string, data: {
    name: string;
    email: string;
    phone?: string;
    photoURL?: string;
  }): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      
      // Check if user already exists
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        return;
      }

      await setDoc(userRef, {
        ...data,
        role: 'user',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

    } catch (error) {
      throw new Error('Failed to create user profile');
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(
    userId: string,
    data: Partial<Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'role'>>
  ): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw new Error('Failed to update user profile');
    }
  }

  /**
   * Get multiple users by IDs (for batch fetching)
   */
  async getUsersByIds(userIds: string[]): Promise<Map<string, User>> {
    try {
      const userMap = new Map<string, User>();

      // Fetch all users in parallel
      const promises = userIds.map(async (userId) => {
        const profile = await this.getUserProfile(userId);
        if (profile) {
          userMap.set(userId, profile);
        }
      });

      await Promise.all(promises);
      return userMap;
    } catch (error) {
      console.error('Error fetching multiple users:', error);
      throw new Error('Failed to fetch users');
    }
  }
}

export const userService = new UserService();